import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const bootstrap = async () => {
            const token = localStorage.getItem("aduku_token");
            if (!token) { setLoading(false); return; }
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
                // v79.81 — Sliding-session refresh. Backend hands back a
                // fresh 90-day token in the response body when the
                // current one has <30 days of life left. Persist it so
                // subsequent axios requests use the extended lifetime.
                if (data?.refreshed_token) {
                    try { localStorage.setItem("aduku_token", data.refreshed_token); } catch { /* private mode */ }
                }
            } catch (err) {
                // v79.81 — CRITICAL FIX: only clear the token on a genuine
                // 401 (Unauthorized / Token invalid). Previously ANY error
                // — network timeout, 502 during pod restart, offline
                // Android WebView, DNS blip — wiped the token and forced
                // re-login. On flaky mobile networks this happened
                // constantly. Now the token is preserved and the next
                // successful call (auto-retry or user action) revalidates.
                const status = err?.response?.status;
                if (status === 401) {
                    localStorage.removeItem("aduku_token");
                }
                // For all other errors (network / 5xx / abort): keep the
                // token and leave `user` as null; the API interceptor + a
                // subsequent nav will re-attempt automatically.
            } finally {
                setLoading(false);
            }
        };
        bootstrap();
        const onUpdate = (e) => { if (e?.detail) setUser(e.detail); };
        window.addEventListener("aduku:user-updated", onUpdate);
        return () => window.removeEventListener("aduku:user-updated", onUpdate);
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("aduku_token", data.token);
        setUser({ id: data.id, name: data.name, email: data.email });
        return data;
    };

    const register = async (name, email, password, extras = {}) => {
        const { data } = await api.post("/auth/register", { name, email, password, ...extras });
        localStorage.setItem("aduku_token", data.token);
        setUser({
            id: data.id, name: data.name, email: data.email,
            account_type: data.account_type, shop_name: data.shop_name, gstin: data.gstin,
        });
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("aduku_token");
        setUser(null);
    };

    /**
     * Exchange the Emergent OAuth `session_id` (returned in the URL fragment after
     * the user finishes Google's consent screen) for our own JWT. The backend
     * find-or-creates a user by email and sets the access_token cookie.
     */
    const completeGoogleSignIn = async (sessionId) => {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        localStorage.setItem("aduku_token", data.token);
        const { data: me } = await api.get("/auth/me");
        setUser(me);
        return me;
    };

    const refreshUser = async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            // v79.81 — Persist sliding-refresh token.
            if (data?.refreshed_token) {
                try { localStorage.setItem("aduku_token", data.refreshed_token); } catch { /* private mode */ }
            }
            return data;
        } catch {
            return null;
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, completeGoogleSignIn, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
